import { Tenant } from "@models";
import { useStore } from "@stores";
import { Button, Row } from "antd";
import { observer } from "mobx-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TenantCard } from "./card/TenantCard";
import { TenantSkeletonCard } from "./card/TenantSkeletonCard";
import CommentsDrawer from "./CommentsDrawer.container";
import { ResumeDrawer } from "./ResumeDrawer";
import { SharedEmpty } from "./SharedEmpty";

const SKELETON_COUNT = 6;

const ResultsRow = styled(Row)<{ $busy?: boolean }>`
  opacity: ${(p) => (p.$busy ? 0.45 : 1)};
  pointer-events: ${(p) => (p.$busy ? "none" : "auto")};
  transition: opacity 0.2s ease-out;
`;

const LoadMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

interface Props {
  tenants: Tenant[];
  /** Prvo učitavanje ili ponovno učitavanje posle promene filtera. */
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

/**
 * Lista kartica salona sa svim što ide uz nju: skeleton pri prvom učitavanju,
 * prazno stanje, "Učitaj još", omiljeni i prozori sa recenzijama/opisom. Koriste
 * je lista po kategoriji i stranica grada, da se ponašaju identično.
 */
export const TenantResults = observer(
  ({ tenants, loading, loadingMore, hasMore, onLoadMore }: Props) => {
    const { t } = useTranslation();
    const { favoriteStore, authStore } = useStore();
    const [reviewsTenant, setReviewsTenant] = useState<Tenant | null>(null);
    const [resumeTenant, setResumeTenant] = useState<Tenant | null>(null);

    if (loading && !tenants.length) {
      return (
        <Row gutter={[16, 16]} justify="center">
          {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
            <TenantSkeletonCard key={index} index={index} />
          ))}
        </Row>
      );
    }

    return (
      <>
        {/* Pri promeni grada ili pretrage lista ostaje na ekranu i samo
            pobledi — zamena skeletonima na svaki filter deluje sporije
            nego što jeste. */}
        <ResultsRow gutter={[16, 16]} justify="center" $busy={loading}>
          {tenants.map((tenant, index) => (
            <TenantCard
              key={tenant.id}
              tenant={tenant}
              index={index}
              showFavorite={!!authStore.currentUserId}
              onFavoriteToggle={(selected) => {
                const prev = selected.isFavorite;
                selected.isFavorite = !prev;
                favoriteStore.toggle(selected.id).catch(() => {
                  selected.isFavorite = prev;
                });
              }}
              onReviewsClick={setReviewsTenant}
              onResumeClick={setResumeTenant}
            />
          ))}
        </ResultsRow>

        {!tenants.length && !loading && (
          <SharedEmpty description={t("NoData")} />
        )}

        {hasMore && (
          <LoadMoreWrapper>
            <Button type="primary" onClick={onLoadMore} loading={loadingMore}>
              {t("LoadMore")}
            </Button>
          </LoadMoreWrapper>
        )}

        {reviewsTenant && (
          <CommentsDrawer
            open={!!reviewsTenant}
            onClose={() => setReviewsTenant(null)}
            tenantId={reviewsTenant.id}
            averageRate={reviewsTenant.rate}
            rateCount={reviewsTenant.rateCount}
          />
        )}

        {resumeTenant && (
          <ResumeDrawer
            open={!!resumeTenant}
            onClose={() => setResumeTenant(null)}
            tenant={resumeTenant}
          />
        )}
      </>
    );
  },
);
